import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Upload, FileText, ImageIcon, FileSpreadsheet, Download, Trash2, Loader2, File, Ruler } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function iconFor(ext) {
  if (["png", "jpg", "jpeg", "gif", "webp", "tif", "tiff", "svg"].includes(ext)) return ImageIcon;
  if (["pdf"].includes(ext)) return FileText;
  if (["csv", "xls", "xlsx"].includes(ext)) return FileSpreadsheet;
  if (["dwg", "dxf"].includes(ext)) return Ruler;
  return File;
}
const kb = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function FilesPanel({ contactId, jobId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const q = contactId ? `contact_id=${contactId}` : `job_id=${jobId}`;
  const load = () => api.get(`/files?${q}`).then((r) => setFiles(r.data)).catch(() => {});
  useEffect(() => { load(); }, [contactId, jobId]);

  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file);
      if (contactId) fd.append("contact_id", contactId);
      if (jobId) fd.append("job_id", jobId);
      try { await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch (err) { toast.error(`${file.name}: ${err.response?.data?.detail || "upload failed"}`); }
    }
    toast.success(`${list.length} file${list.length > 1 ? "s" : ""} uploaded`);
    setUploading(false); e.target.value = ""; load();
  };

  const download = (f) => {
    const token = localStorage.getItem("mb_token");
    window.open(`${BACKEND}/api/files/${f.id}/download?auth=${token}`, "_blank");
  };
  const remove = async (f) => { await api.delete(`/files/${f.id}`); toast.success("File removed"); load(); };

  return (
    <div className="mb-card p-5">
      <input ref={inputRef} type="file" multiple onChange={upload} className="hidden"
        accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.tif,.tiff,.svg,.csv,.txt,.doc,.docx,.xls,.xlsx,.zip,.dwg,.dxf" data-testid="files-input" />
      <button data-testid="upload-doc-btn" onClick={() => inputRef.current?.click()} disabled={uploading}
        className="w-full h-14 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center gap-2 text-slate-500 hover:border-navy hover:text-navy transition-colors font-semibold disabled:opacity-60">
        {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
        {uploading ? "Uploading…" : "Add Blueprints & Documents (PDF, DWG, DXF, image, ZIP · up to 50MB)"}
      </button>

      <div className="mt-4 space-y-2">
        {files.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No documents yet. Upload contracts, aerial reports, photos or invoices.</p>}
        {files.map((f) => {
          const Icon = iconFor(f.ext);
          return (
            <div key={f.id} data-testid={`file-${f.id}`} className="flex items-center gap-3 border border-slate-200 rounded-xl px-3 py-2.5 group">
              <div className="w-9 h-9 rounded-lg bg-navy/10 flex items-center justify-center shrink-0"><Icon className="w-4.5 h-4.5 text-navy" style={{ width: 18, height: 18 }} /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-navy truncate">{f.original_filename}</div>
                <div className="text-xs text-slate-400">{kb(f.size)} · {fmtDate(f.created_at)} · {f.uploaded_by}</div>
              </div>
              <button data-testid={`download-${f.id}`} onClick={() => download(f)} className="text-slate-400 hover:text-navy p-1.5"><Download className="w-4 h-4" /></button>
              <button data-testid={`delete-${f.id}`} onClick={() => remove(f)} className="text-slate-400 hover:text-dangerr p-1.5"><Trash2 className="w-4 h-4" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
